import { v4 as uuidv4 } from 'uuid'
import { ArticleBusinessType } from 'admin/articleService'
import { InventoryPoolRole } from 'inventory/inventoryService'
import { CapacityDetails, CapacityHealth, CapacitySummary } from 'capacity/types'
import {
    CapacityGateway,
    CapacityMember,
    CapacityMemberCounts,
    CapacityMembers,
    CapacityNotSupportedError,
    GatewayCapabilities,
    ProductOp,
    SavePlan,
    SaveResult,
    splitVisitorCapacities,
    TodayUsage,
    VISITOR_KINDS,
    VisitorCapacities,
    VisitorKind,
} from './capacityGateway'
import {
    CapacityV2Error,
    CapacityV2Service,
    V2CapacityDetail,
    V2CapacityRow,
    V2LinkChange,
    V2LinkDelta,
    V2Member,
    V2Role,
} from './capacityV2Service'

export const SERVER_CAPABILITIES: GatewayCapabilities = {
    list: 'server',
    members: 'server',
    optionItemsCap: null,
    atomicSave: true,
    delete: false,
    usage: 'none',
    health: 'server',
    visitors: 'server',
    timeSlots: 'server',
}

const ROLE_FROM_V2: Record<Exclude<V2Role, null>, InventoryPoolRole> = {
    admission: 'Admission',
    undated_admission: 'UndatedAdmission',
    non_admission: 'NonAdmission',
}

const VISITOR_ROLE_OF_KIND: Record<VisitorKind, Exclude<V2Role, null>> = {
    dated: 'admission',
    undated: 'undated_admission',
}

export const summaryFromV2 = (accountSlug: string, row: V2CapacityRow): CapacitySummary => ({
    id: row.id,
    name: row.name,
    account: accountSlug,
    defaultAvailability: row.defaultAvailability,
    reservationPeriodType: row.reservationPeriodType,
    role: row.role ? ROLE_FROM_V2[row.role] : null,
    health: row.health,
})

export const detailsFromV2 = (accountSlug: string, detail: V2CapacityDetail): CapacityDetails => ({
    ...summaryFromV2(accountSlug, detail),
    version: detail.version,
    availabilityRules: detail.exceptions.map((rule) => ({
        id: rule.id,
        name: rule.name,
        availability: rule.availability,
        priority: rule.priority,
        effectiveFrom: rule.effectiveFrom,
        effectiveTo: rule.effectiveTo,
        timeFrom: rule.timeFrom,
        timeTo: rule.timeTo,
        weekdays: rule.weekdays,
    })),
})

const nameOf = (names: Record<string, string>, fallback: string): string =>
    names.en || Object.values(names)[0] || fallback

export const memberFromV2 = (member: V2Member): CapacityMember => {
    if (member.kind === 'option_item') {
        return {
            kind: 'optionItem',
            id: member.id,
            name: nameOf(member.name, member.id),
            businessType: 'upsell',
            isBundle: false,
            categoryIds: [],
            inactiveCategoryIds: [],
            links: member.links,
            optionGroup: { uuid: member.optionGroup.uuid, name: nameOf(member.optionGroup.name, '') },
        }
    }
    return {
        kind: 'product',
        id: member.id,
        name: nameOf(member.name, member.id),
        businessType: (member.businessType as ArticleBusinessType | null) ?? null,
        isBundle: member.isBundle,
        categoryIds: member.categoryIds,
        inactiveCategoryIds: [],
        links: member.links,
    }
}

export function deltaFromOps(ops: ProductOp[]): V2LinkDelta {
    const add: V2LinkChange[] = []
    const update: V2LinkChange[] = []
    const remove: V2LinkChange[] = []
    for (const op of ops) {
        switch (op.kind) {
            case 'add':
                add.push({ kind: 'product', id: op.productId, productListId: null, amountUsed: op.amountUsed ?? 1 })
                break
            case 'setAmount':
                update.push({ kind: 'product', id: op.productId, productListId: null, amountUsed: op.amountUsed ?? 1 })
                break
            case 'remove': {
                const links =
                    op.expectedLinks && op.expectedLinks.length > 0 ? op.expectedLinks : [{ productListId: null }]
                for (const link of links) {
                    remove.push({ kind: 'product', id: op.productId, productListId: link.productListId })
                }
                break
            }
            default:
                break
        }
    }
    const delta: V2LinkDelta = {}
    if (add.length) delta.add = add
    if (update.length) delta.update = update
    if (remove.length) delta.remove = remove
    return delta
}

const errorOf = (error: unknown): NonNullable<SaveResult['error']> => {
    if (
        error instanceof CapacityV2Error ||
        (error instanceof Error && typeof (error as CapacityV2Error).status === 'number')
    ) {
        const status = (error as CapacityV2Error).status
        if (status === 412) {
            return { code: 'conflict', message: error.message }
        }
        if (status === 503) {
            return { code: 'unavailable', message: error.message }
        }
        return { code: 'failed', message: error.message }
    }
    return { code: 'failed', message: error instanceof Error ? error.message : 'Something went wrong' }
}

const failedResult = (plan: SavePlan, capacityId: string | null, created: boolean, error: unknown): SaveResult => {
    const failure = errorOf(error)
    return {
        capacityId,
        created,
        capacitySaved: created || capacityId !== null,
        succeeded: [],
        failed: plan.productOps.map((op) => ({ op, message: failure.message })),
        error: failure,
    }
}

export class ServerCapacityGateway implements CapacityGateway {
    readonly capabilities = SERVER_CAPABILITIES

    constructor(private v2: CapacityV2Service) {}

    async listCapacities(accountSlug: string): Promise<CapacitySummary[]> {
        const rows = await this.v2.listCapacities(accountSlug)
        return rows.map((row) => summaryFromV2(accountSlug, row))
    }

    async getCapacity(accountSlug: string, capacityId: string): Promise<CapacityDetails> {
        return detailsFromV2(accountSlug, await this.v2.getCapacity(accountSlug, capacityId))
    }

    async getMembers(accountSlug: string, capacityId: string): Promise<CapacityMembers> {
        const members = await this.v2.listMembers(accountSlug, capacityId)
        return { capacityId, members: members.map(memberFromV2), optionItems: 'full' }
    }

    async getMemberCounts(accountSlug: string): Promise<CapacityMemberCounts> {
        const rows = await this.v2.listCapacities(accountSlug)
        const counts: CapacityMemberCounts['counts'] = {}
        for (const row of rows) {
            counts[row.id] = { products: row.productsCount, optionItems: row.optionItemsCount }
        }
        return { counts, optionItems: 'full' }
    }

    async save(accountSlug: string, plan: SavePlan): Promise<SaveResult> {
        let capacityId = plan.capacityId
        let created = false
        if (plan.capacity) {
            const edited = {
                name: plan.capacity.body.name,
                defaultAvailability: plan.capacity.body.defaultAvailability ?? null,
            }
            try {
                if (plan.capacity.kind === 'create') {
                    const id = uuidv4()
                    await this.v2.putCapacity(
                        accountSlug,
                        id,
                        { ...edited, reservationPeriodType: plan.capacity.body.reservationPeriodType },
                        { ifNoneMatch: '*' },
                    )
                    capacityId = id
                    created = true
                } else {
                    const record = plan.capacity.body
                    const version = record.version ?? (await this.v2.getCapacity(accountSlug, record.id)).version
                    await this.v2.putCapacity(accountSlug, record.id, edited, { ifMatch: `"${version}"` })
                }
            } catch (error) {
                return {
                    capacityId: plan.capacityId,
                    created: false,
                    capacitySaved: false,
                    succeeded: [],
                    failed: plan.productOps.map((op) => ({ op, message: errorOf(error).message })),
                    error: errorOf(error),
                }
            }
        }
        if (!capacityId) {
            return {
                capacityId: null,
                created,
                capacitySaved: !!plan.capacity,
                succeeded: [],
                failed: plan.productOps.map((op) => ({ op, message: 'No capacity to add the product to' })),
            }
        }
        if (plan.productOps.length === 0) {
            return { capacityId, created, capacitySaved: true, succeeded: [], failed: [] }
        }
        try {
            await this.v2.patchMembers(accountSlug, capacityId, deltaFromOps(plan.productOps))
        } catch (error) {
            return failedResult(plan, capacityId, created, error)
        }
        return { capacityId, created, capacitySaved: true, succeeded: plan.productOps, failed: [] }
    }

    async deleteCapacity(): Promise<void> {
        throw new CapacityNotSupportedError('BUIL-800', 'Capacities cannot be deleted yet.')
    }

    async getTodayUsage(_accountSlug: string, capacityIds: string[]): Promise<Record<string, TodayUsage>> {
        return capacityIds.reduce<Record<string, TodayUsage>>((all, id) => {
            all[id] = { status: 'unavailable', limit: null, used: null, left: null, basis: 'server' }
            return all
        }, {})
    }

    getHealth(details: CapacityDetails): CapacityHealth {
        if (details.health === 'no_default_rule') {
            return 'noDefaultRule'
        }
        return details.health === 'ok' ? 'ok' : 'unknown'
    }

    visitorKindOf(capacity: CapacitySummary): VisitorKind | null {
        return VISITOR_KINDS.find((kind) => capacity.role === ROLE_FROM_V2[VISITOR_ROLE_OF_KIND[kind]]) ?? null
    }

    async getVisitorCapacities(accountSlug: string): Promise<VisitorCapacities> {
        const capacities = await this.listCapacities(accountSlug)
        return splitVisitorCapacities(capacities, (capacity) => this.visitorKindOf(capacity)).visitors
    }

    async createVisitorCapacity(accountSlug: string, kind: VisitorKind): Promise<CapacitySummary> {
        const result = await this.v2.ensureVisitorCapacities(accountSlug)
        return summaryFromV2(accountSlug, result[kind])
    }
}
